<script lang="ts" setup>
import type { Suggestion, SuggestionGroupKey } from '.';
import type { SearchSuggestDialogPanelHostProps } from './searchSuggestDialogPanelProps';
import type { SuggestionGroup } from '~/composables/useLocationAutocomplete';

import {
	Command,
	CommandDialog,
	CommandList,
	CommandSeparator
} from '@/components/SearchSuggestRadixCommand';
import { cn } from '~/utils';

import IconMaterialSymbolsLightArrowLeftAlt from '~icons/material-symbols-light/arrow-left-alt';
import HistoryGroupIcon from '~icons/material-symbols-light/history';

import CodeGroup from './CodeGroup.vue';
import LoadingGroup from './LoadingGroup.vue';
import NewGroup from './NewGroup.vue';
import NewSuggestInput from './NewSuggestInput.vue';
import SearchSuggestListContent from './SearchSuggestListContent.vue';

const props = withDefaults(defineProps<SearchSuggestDialogPanelHostProps>(), {
	portalWrapperClass: 'fixed inset-0 z-[40] h-dvh w-dvw max-w-none'
});

const listSkeletonVariant = computed(() =>
	props.filterProps.variant === 'dark' ? 'primary' : 'white'
);

const searchTerm = defineModel<string>('searchTerm', { required: true });
const modelValue = defineModel<Suggestion | undefined>('modelValue', {
	required: true
});
const selectedValue = defineModel<SuggestionGroup | undefined>('selectedValue');

const inputEl = useTemplateRef<InstanceType<typeof NewSuggestInput>>('inputEl');
defineExpose({ inputEl });

const mergedOverlayClass = computed(() =>
	cn(props.variantStyles.dialogOverlayClass, props.dialogOverlayExtraClass)
);

const mergedContentClass = computed(() =>
	cn(props.variantStyles.dialogContentClass, props.dialogContentExtraClass)
);
</script>
<template>
	<ClientOnly>
		<Teleport to="body">
			<Transition name="slide-up">
				<div v-if="isOpen" :class="props.portalWrapperClass">
					<CommandDialog
						:open="isOpen"
						class="h-dvh w-dvw max-w-none"
						:content-class="mergedContentClass"
						:overlay-class="mergedOverlayClass"
						:show-close-icon="false"
						title="Inicie sua busca"
						hide-title
						@update:open="onCommandOpenChange"
					>
						<div :class="variantStyles.dialogMainWrapper">
							<Command
								v-model="modelValue"
								v-model:search-term="searchTerm"
								v-model:selected-value="selectedValue"
								:display-value="getDisplayValue"
								reset-search-term-on-blur
								reset-search-term-on-select
								:class="variantStyles.dialogCommand"
								@update:model-value="onModelValueUpdate($event as Suggestion)"
								@keydown="onKeyDown"
							>
								<NewSuggestInput
									ref="inputEl"
									:placeholder="dialogPlaceholder"
									:focused-placeholder="dialogFocusedPlaceholder"
									:enable-animation="false"
									:class="cn(variantStyles.dialogInput, inputClasses)"
									:input-clear-icon-classes="variantStyles.dialogInputClearIcon"
									:input-search-icon-classes="
										variantStyles.dialogInputSearchIcon
									"
									:model-value="searchTerm"
									@clear="onDialogInputClear"
								>
									<template #prefix>
										<div class="ml-5 flex size-6 items-center justify-center">
											<IconMaterialSymbolsLightArrowLeftAlt
												:class="variantStyles.dialogBackIcon"
												@click="onDialogBack"
											/>
										</div>
									</template>
								</NewSuggestInput>
								<CommandSeparator :class="variantStyles.dialogSeparator" />

								<CommandList
									:class="
										cn(
											'h-full max-h-none border border-t-0 p-0 transition-all duration-300 ease-out',
											variantStyles.listWrapper,
											loading ? 'overflow-hidden!' : 'overflow-y-auto'
										)
									"
									data-scroll-area
								>
									<SearchSuggestListContent
										:skeleton-variant="listSkeletonVariant"
										:loading="loading"
										:should-show-empty-state="shouldShowEmptyState"
										:should-show-last-researchs="shouldShowLastResearchs"
										:should-show-code-group="shouldShowCodeGroup"
										:has-min-search-term="hasMinSearchTerm"
										:search-term="searchTerm"
										:is-fetching-property-by-code-search="
											filterProps.isFetchingPropertyByCodeSearch
										"
										:filtered-last-researchs="filteredLastResearchs"
										:location-autocomplete-data="locationAutocompleteData"
										:search-by-code-option="searchByCodeOption"
										:empty-state-wrapper-classes="
											filterProps.emptyStateWrapperClasses
										"
										:empty-state-icon-wrapper-classes="
											filterProps.emptyStateIconWrapperClasses
										"
										:empty-state-icon-classes="
											filterProps.emptyStateIconClasses
										"
										:empty-state-title-classes="
											filterProps.emptyStateTitleClasses
										"
										:empty-state-description-classes="
											filterProps.emptyStateDescriptionClasses
										"
										:separator-classes="variantStyles.dialogSeparator"
										:group-heading-classes="variantStyles.groupHeading"
										:group-icon-classes="variantStyles.groupIcon"
										:group-item-wrapper-classes="variantStyles.groupItemWrapper"
										:group-item-text-classes="variantStyles.groupItemText"
										:group-item-sub-text-classes="
											variantStyles.groupItemSubText
										"
										:variant-styles="variantStyles"
										@remove-suggestion="onRemoveResearch"
									/>
								</CommandList>
							</Command>
						</div>
					</CommandDialog>
				</div>
			</Transition>
		</Teleport>
	</ClientOnly>
</template>
